import { useEffect, useRef, useState } from "react";
import { Popup } from "devextreme-react";
import { ButtonType } from "devextreme/common";
import Form, { ButtonItem, FormRef, GroupItem, RequiredRule, SimpleItem } from "devextreme-react/form";

import { Utilities } from "@/shared/utils/utilities";
import { EmailDetail } from "@/shared/models/EmailDetail";
import { EmailTemplate } from "@/shared/models/EmailTemplate";
import { useFormCloseConfirm } from "@/hooks/useFormCloseConfirm";
import { ReviewSendEmailsApi } from "@/shared/api/ReviewSendEmails.api";
import { SurchargeEventCalculation } from "@/shared/models/SurchargeEventCalculation";
import { SurchargeEventCalculationRun } from "@/shared/models/SurchargeEventCalculationRun";
import HelpTable from "./HelpTable";
import "./style.css";

interface Props {
  visible: boolean;
  emailDetail: EmailDetail;
  closeForm: (reloadData: boolean) => Promise<void>;
  selectedSurchargeEvent: SurchargeEventCalculation;
  selectedCalculatedEventRun: SurchargeEventCalculationRun;
}

const EmailDetailForm = ({ visible, emailDetail, closeForm, selectedSurchargeEvent, selectedCalculatedEventRun }: Props) => {
  const formRef = useRef<FormRef>(null);
  const { setInitialFormData, canCloseForm } = useFormCloseConfirm({} as any);

  const [formData, setFormData] = useState({} as any);
  const [localEmailDetail, setLocalEmailDetail] = useState(emailDetail);
  const [editing, setEditing] = useState(false);
  const [sent, setSent] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [relaod, setReload] = useState(false);
  const [allTemplates, setAllTemplates] = useState<EmailTemplate[]>([]);
  const [htmlTemplateValue, setHtmlTemplateValue] = useState(emailDetail.template);

  useEffect(() => {
    if (!emailDetail) {
      return;
    }

    var data = {
      recipients: emailDetail.recipients,
      name: emailDetail.name,
      importObject: "",
      template: emailDetail.template,
    };
    setFormData(data);
    setInitialFormData(data);

    setSent(emailDetail.sent);
    setLocalEmailDetail(emailDetail);
  }, [emailDetail]);

  const handleImportFromChanged = (e: any) => {
    if (!e || !e.value) {
      return;
    }

    var selectedTemplate = allTemplates.filter((item) => item.name === e.value)[0];
    var data = {
      ...formData,
      recipients: selectedTemplate.recipients,
      name: localEmailDetail.customerName,
      template: selectedTemplate.template,
    };
    setFormData(data);
    setInitialFormData(data);
  };

  const handleSaveTemplate = async (e: any) => {
    e.preventDefault();
    if (
      await ReviewSendEmailsApi.saveTemplate({
        Html: formData.template,
        Recipients: formData.recipients,
        Name: formData.name,

        Subject: localEmailDetail.subject,
        SurchargeEventCalculationId: localEmailDetail.surchargeEventCalculationId,
        ShipTo: localEmailDetail.shipTo,
        User: localEmailDetail.createdBy,
        FromTemplate: localEmailDetail.fromTemplate || "00000000-0000-0000-0000-000000000000",
      })
    ) {
      setReload(true);
      await handleRegenerate();
    }
  };

  const handleRegenerate = async () => {
    var data = await ReviewSendEmailsApi.regenerateEmails(selectedSurchargeEvent.id, selectedCalculatedEventRun.id, localEmailDetail.shipTo);
    if (data) {
      setEditing(false);
      var newData = {
        ...formData,
        recipients: data.recipients,
        template: data.fromTemplate,
        importObject: "",
      };
      setFormData(newData);
      setInitialFormData(newData);

      setLocalEmailDetail({
        ...localEmailDetail,
        recipients: data.recipients,
        template: data.fromTemplate,
        html: data.html,
      });

      setReload(true);
    }
  };

  const handleShowHelp = () => setShowHelp((item) => !item);

  const handleViewTemplate = async () => {
    setEditing(true);
    !localEmailDetail.template && setAllTemplates(await ReviewSendEmailsApi.getAllTemplates());
  };

  const handleMarkAsSend = async () => {
    if (await ReviewSendEmailsApi.markAsSentOrUnsend(localEmailDetail, true)) {
      setSent(true);
      setReload(true);
    }
  };

  const handleUnMarkAsSend = async () => {
    if (await ReviewSendEmailsApi.markAsSentOrUnsend(localEmailDetail, false)) {
      setSent(false);
      setReload(true);
    }
  };

  const handleCancel = async () => {
    if (editing) {
      setEditing(false);
    } else {
      canCloseForm(formData) && closeForm && (await closeForm(relaod));
    }
  };

  return (
    <Popup
      width="80%"
      height="90%"
      showTitle={true}
      title={"Review, Edit and Send Email"}
      dragEnabled={true}
      hideOnOutsideClick={false}
      visible={visible}
      onHiding={async (e) => {
        e.cancel = true;
        await handleCancel();
      }}
      showCloseButton={true}
    >
      <form onSubmit={handleSaveTemplate}>
        <Form ref={formRef} labelMode="floating" formData={formData} colCount={2} showColonAfterLabel={true}>
          {!localEmailDetail.html && !editing ? (
            <></>
          ) : (
            <>
              <GroupItem>
                <SimpleItem
                  label={{ text: "Recipients:" }}
                  editorOptions={{
                    disabled: !editing,
                  }}
                  dataField="recipients"
                  editorType="dxTextBox"
                >
                  <RequiredRule message="Recipients is required" />
                </SimpleItem>
              </GroupItem>
              <GroupItem>
                <GroupItem colCount={formData.template ? 1 : 2}>
                  <SimpleItem
                    label={{ text: "Template Name:" }}
                    editorOptions={{
                      disabled: !editing,
                    }}
                    dataField="name"
                    editorType="dxTextBox"
                  >
                    <RequiredRule message="Template Name is required" />
                  </SimpleItem>
                  {!formData.template && (
                    <SimpleItem
                      label={{ text: "Import from:" }}
                      editorOptions={{
                        items: allTemplates.map((item) => item.name),
                        onValueChanged: handleImportFromChanged,
                      }}
                      dataField="importObject"
                      editorType="dxSelectBox"
                    />
                  )}
                </GroupItem>
              </GroupItem>
            </>
          )}
          <GroupItem colSpan={2}>
            {!editing &&
              (Utilities.isNullOrEmptyString(localEmailDetail.html) ? (
                <div className="text-danger">No template provided! Click "View Template" to create one</div>
              ) : (
                <div className="border p-2" dangerouslySetInnerHTML={{ __html: localEmailDetail.html }} />
              ))}
          </GroupItem>

          {editing && (
            <>
              <GroupItem>
                <div className="border p-2 mt-2 edit-template-left" dangerouslySetInnerHTML={{ __html: htmlTemplateValue }}></div>
              </GroupItem>
              <GroupItem>
                <SimpleItem
                  cssClass="edit-template-right"
                  label={{ text: "Edit Template:" }}
                  editorOptions={{
                    height: 780, // height in pixels
                    onValueChanged: (e: any) => {
                      setHtmlTemplateValue(e.value);
                    },
                  }}
                  dataField={"template"}
                  editorType="dxTextArea"
                >
                  <RequiredRule message="Template is required" />
                </SimpleItem>
              </GroupItem>
            </>
          )}

          {editing && (
            <>
              <GroupItem colSpan={2}>
                <ButtonItem
                  cssClass="text-start"
                  buttonOptions={{
                    text: showHelp ? "HIDE HELP" : "SHOW HELP",
                    type: "default" as ButtonType,
                    width: 120,
                    onClick: handleShowHelp,
                  }}
                />
              </GroupItem>
              <GroupItem colSpan={2}>{showHelp && <HelpTable />}</GroupItem>
            </>
          )}

          <GroupItem colSpan={2}>{sent && <div className="mt-2 bg-warning">This email has been marked as "sent"</div>}</GroupItem>

          <GroupItem
            colSpan={2}
            cssClass="multi-buttons-form-container mt-4"
            colCountByScreen={{
              xs: 1,
              sm: 1,
              md: 2,
              lg: 4,
            }}
          >
            {!editing && (
              <ButtonItem
                buttonOptions={{
                  text: "REGENERATE FROM TEMPLATE",
                  type: "danger" as ButtonType,
                  width: "100%",
                  disabled: sent,
                  onClick: async () => await handleRegenerate(),
                }}
              />
            )}

            {!editing && (
              <ButtonItem
                buttonOptions={{
                  type: "default" as ButtonType,
                  text: "VIEW TEMPLATE",
                  width: "100%",
                  onClick: handleViewTemplate,
                }}
              />
            )}

            {editing && (
              <ButtonItem
                buttonOptions={{
                  type: "default" as ButtonType,
                  text: "SAVE TEMPLATE",
                  width: "100%",
                  useSubmitBehavior: true,
                  disabled: sent,
                }}
              />
            )}

            {!editing && !sent && (
              <ButtonItem
                buttonOptions={{
                  type: "default" as ButtonType,
                  text: "MARK AS SENT",
                  width: "100%",
                  onClick: handleMarkAsSend,
                  disabled: sent,
                }}
              />
            )}

            {!editing && sent && (
              <ButtonItem
                buttonOptions={{
                  type: "default" as ButtonType,
                  text: "UN-MARK AS SENT",
                  width: "100%",
                  onClick: handleUnMarkAsSend,
                }}
              />
            )}

            <ButtonItem
              buttonOptions={{
                text: "CANCEL",
                width: "100%",
                onClick: handleCancel,
              }}
            />
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
};

export default EmailDetailForm;
